/**
 * The welcome tour: one card per area of the CRM, in plain and slightly silly
 * words (owner, 2026-11-04: "really simple and funny language").
 *
 * A step with a `target` spotlights that element; a step whose target is not
 * on screen (the role has no such tab, the integration isn't connected) is
 * SKIPPED, so nobody is shown a door they can't open. A step with no target
 * is a centred card.
 */

export type TourPlacement = "right" | "bottom" | "left" | "top";

export interface TourStep {
  id: string;
  /** CSS selector of the element to spotlight. None = a centred card. */
  target?: string;
  /** Page to open before looking for the target. */
  route?: string;
  placement?: TourPlacement;
  emoji: string;
  title: string;
  body: string;
}

/** A sidebar link (both the icon rail and the pinned footer render `<a href>`). */
export const sidebarLink = (path: string) => `[data-sidebar="sidebar"] a[href="${path}"]`;
const nav = sidebarLink;

export function buildTourSteps(opts: {
  firstName: string;
  isLeadManagement: boolean;
}): TourStep[] {
  const { firstName, isLeadManagement } = opts;
  return [
    {
      id: "welcome",
      emoji: "👋",
      title: firstName ? `Hey ${firstName}, welcome to Xale!` : "Hey, welcome to Xale!",
      body: "This is your new sales HQ. Give us 2 minutes and you'll know your way around better than your office Wi-Fi password.",
    },
    {
      id: "sidebar",
      target: '[data-sidebar="sidebar"]',
      placement: "right",
      emoji: "🎛️",
      title: "Your remote control",
      body: "Everything in Xale lives on this left bar. Hover any icon to see its name. No guessing games.",
    },
    {
      id: "dashboard",
      target: nav("/"),
      placement: "right",
      emoji: "☕",
      title: "Dashboard: your morning chai",
      body: "One look tells you how many leads came in, who is waiting for a call today and how the team is doing. Best enjoyed with actual chai.",
    },
    {
      id: "leads",
      target: nav("/leads"),
      placement: "right",
      emoji: "💰",
      title: "Leads: where the money lives",
      body: "Every enquiry lands here: your website, Facebook, WhatsApp, phone calls, or typed in by hand. Think of it as a notebook that never forgets.",
    },
    {
      id: "add-lead",
      route: "/leads",
      target: ".addLeadbtn",
      placement: "bottom",
      emoji: "➕",
      title: "Add a lead in 10 seconds",
      body: "Got a name and a phone number? Click here, fill it in, done. Faster than making Maggi.",
    },
    {
      id: "search",
      // The header's search box is always the first thing in its tool row.
      target: '[data-tour="header-tools"] > :first-child',
      placement: "bottom",
      emoji: "🔍",
      title: "Lost someone? Just search",
      body: "Type a name, a phone number or anything you remember about them. Xale digs it up.",
    },
    {
      id: "stages",
      target: nav("/stage-management"),
      placement: "right",
      emoji: "🛣️",
      title: "Stages: your sales journey",
      body: isLeadManagement
        ? "Every lead walks the same road: New → In Talks → Negotiation → Won. Rename the stops, add new ones, make the road yours."
        : "Every lead walks a road, from 'just said hi' to 'deal done'. Rename the stops, add new ones, make the road yours.",
    },
    {
      id: "users",
      target: nav("/user-management"),
      placement: "right",
      emoji: "👯",
      title: "Bring your squad",
      body: "Add your teammates and give each one a role. They only see what you allow. Zero drama, zero 'who deleted my lead?'.",
    },
    {
      id: "sources",
      target: nav("/source-management"),
      placement: "right",
      emoji: "🧲",
      title: "Lead magnets",
      body: "Connect your website, Facebook and Instagram ads, WhatsApp and more. New leads then walk in on their own. Like magic, minus the wand.",
    },
    {
      id: "custom-fields",
      target: nav("/custom-field-management"),
      placement: "right",
      emoji: "📝",
      title: "Save the details that matter",
      body: "Budget, city, best time to call… add any field you need to a lead. Your CRM, your rules.",
    },
    {
      id: "automation",
      target: nav("/automation"),
      placement: "right",
      emoji: "🤖",
      title: "Meet your robot intern",
      body: "Tell Xale: 'when a new lead comes in, give it to Priya and send a WhatsApp'. It works 24×7, never sleeps and never asks for a raise.",
    },
    {
      id: "reports",
      target: nav("/report"),
      placement: "right",
      emoji: "📊",
      title: "Numbers without the headache",
      body: "Which source brings the best leads? Who closed the most? Reports answer in one click. No Excel tears.",
    },
    {
      id: "whatsapp",
      target: nav("/whatsapp"),
      placement: "right",
      emoji: "💬",
      title: "Chat without switching apps",
      body: "Talk to your leads on WhatsApp right here inside Xale. Your phone can finally take a nap.",
    },
    {
      id: "alerts",
      target: '[data-tour="header-notifications"]',
      placement: "bottom",
      emoji: "🔔",
      title: "Pings and reminders",
      body: "A follow-up is due? A new lead arrived? This little bell rings. Like a doorbell, but for money.",
    },
    {
      id: "settings",
      target: nav("/settings"),
      placement: "right",
      emoji: "⚙️",
      title: "The control room",
      body: "Branches, working hours, billing and every other knob. Fiddle away, nothing explodes.",
    },
    {
      id: "support",
      target: 'button[aria-label="Support on WhatsApp"]',
      placement: "bottom",
      emoji: "🎧",
      title: "Stuck? Real humans, one tap away",
      body: "Our team is on WhatsApp. We reply fast and no question is too silly. We've heard worse, trust us.",
    },
    {
      id: "replay",
      target: '[data-tour="header-profile"]',
      placement: "bottom",
      emoji: "🔁",
      title: "Replay any time",
      body: "Forgot something? Click your initials, pick 'Take the tour', and we'll do this all again. Popcorn optional.",
    },
    {
      id: "done",
      emoji: "🎉",
      title: "You're ready to rock!",
      body: "That's the whole tour. Best first move: add your first lead. There's a little checklist at the bottom right to keep you on track.",
    },
  ];
}
